import React, { Suspense, lazy, useCallback, useEffect } from "react";
import { createRoot } from "react-dom/client";
import AdminLayout from "./components/admin/AdminLayout.jsx";
import { ToastProvider } from "./components/admin/ToastProvider.jsx";
import { useCBTStore } from "./store/cbtStore";
import "./styles.css";

const LoginSiswa = lazy(() => import("./pages/LoginSiswa.jsx"));
const GuruDashboard = lazy(() => import("./pages/GuruDashboard.jsx"));
const ManageUjian = lazy(() => import("./pages/ManageUjian.jsx"));
const CreateUjian = lazy(() => import("./pages/CreateUjian.jsx"));
const HasilUjianGuru = lazy(() => import("./pages/HasilUjian.jsx"));
const AnalisisUjian = lazy(() => import("./pages/AnalisisUjian.jsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.jsx"));
const ManageSiswa = lazy(() => import("./pages/ManageSiswa.jsx"));
const ManageGuru = lazy(() => import("./pages/ManageGuru.jsx"));
const BankSoal = lazy(() => import("./pages/BankSoal.jsx"));
const ManageKelas = lazy(() => import("./pages/ManageKelas.jsx"));
const LaporanSistem = lazy(() => import("./pages/LaporanSistem.jsx"));

const staffPageComponents = {
  "guru-dashboard": GuruDashboard,
  "manage-ujian": ManageUjian,
  "create-ujian": CreateUjian,
  "hasil-ujian-guru": HasilUjianGuru,
  "analisis-ujian": AnalisisUjian,
  "admin-dashboard": AdminDashboard,
  "manage-siswa": ManageSiswa,
  "manage-guru": ManageGuru,
  "bank-soal": BankSoal,
  "manage-kelas": ManageKelas,
  "laporan-sistem": LaporanSistem,
};

function LoadingFallback() {
  return (
    <main className="flex min-h-[60vh] items-center justify-center px-4">
      <div
        role="status"
        aria-live="polite"
        className="flex items-center gap-3 rounded-md border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-soft"
      >
        <span className="h-3 w-3 animate-ping rounded-full bg-primary-600" />
        Memuat halaman...
      </div>
    </main>
  );
}

function App() {
  const adminUser = useCBTStore((state) => state.adminUser);
  const currentPage = useCBTStore((state) => state.currentPage);
  const setCurrentPage = useCBTStore((state) => state.setCurrentPage);
  const theme = useCBTStore((state) => state.theme);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {
    if (adminUser && currentPage === "login") {
      setCurrentPage(adminUser.role === "ADMIN" ? "admin-dashboard" : "guru-dashboard");
    }
  }, [adminUser, currentPage, setCurrentPage]);

  const navigate = useCallback(
    (page) => {
      if (!adminUser && page !== "login") {
        setCurrentPage("login");
        return;
      }
      setCurrentPage(page);
    },
    [adminUser, setCurrentPage],
  );

  if (!adminUser) {
    return (
      <ToastProvider>
        <Suspense fallback={<LoadingFallback />}>
          <LoginSiswa />
        </Suspense>
      </ToastProvider>
    );
  }

  const ActiveStaffPage =
    staffPageComponents[currentPage] ??
    (adminUser.role === "ADMIN" ? AdminDashboard : GuruDashboard);

  return (
    <ToastProvider>
      <AdminLayout role={adminUser.role} currentPage={currentPage} onNavigate={navigate}>
        <Suspense fallback={<LoadingFallback />}>
          <ActiveStaffPage onNavigate={navigate} />
        </Suspense>
      </AdminLayout>
    </ToastProvider>
  );
}

createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
